import { useCallback, useEffect, useMemo, useState } from "react";

import { BottomNav } from "@/components/cockpit/BottomNav";
import { CaseCard } from "@/components/cockpit/CaseCard";
import { ContextRail } from "@/components/cockpit/ContextRail";
import { FocusPane } from "@/components/cockpit/FocusPane";
import { RefineOverlay } from "@/components/cockpit/RefineOverlay";
import { Sidebar } from "@/components/cockpit/Sidebar";
import { TaskRow } from "@/components/cockpit/TaskRow";
import { VitalSignsRibbon, type VitalSigns } from "@/components/cockpit/VitalSignsRibbon";
import {
  type CockpitTask,
  type EmailCardData,
  type EmailDetail,
  getEmailDetail,
  getEmails,
  getTasks,
  postSend,
} from "@/lib/emails";

const REFRESH_INTERVAL_MS = 60_000;

interface CockpitState {
  emails: EmailCardData[];
  tasks: CockpitTask[];
  focusEmail: EmailDetail | null;
  loading: boolean;
  error: string | null;
}

/** The Phase 7 desktop cockpit screen (brief §9 / reference image 1).
 *
 *  Layout:
 *  - Left: Sidebar with nav to Cockpit / Lanes / Digest / Tasks.
 *  - Top: VitalSignsRibbon.
 *  - Center: FocusPane (dominant card) + "3 more in queue" stubs.
 *  - Right: ContextRail (entities + shipment + invoice mocks).
 *  - Bottom: TODAY'S TASKS strip.
 */
export function Cockpit() {
  const [state, setState] = useState<CockpitState>({
    emails: [],
    tasks: [],
    focusEmail: null,
    loading: true,
    error: null,
  });
  const [signingOut, setSigningOut] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [refineOpen, setRefineOpen] = useState(false);
  const [draftOverride, setDraftOverride] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [emailsRes, tasksRes] = await Promise.all([
        getEmails(),
        getTasks("open"),
      ]);
      // Flatten the buckets into one list for picking the focus card.
      const allEmails: EmailCardData[] = [
        ...emailsRes.buckets.need_reply_now,
        ...emailsRes.buckets.awaiting_your_action,
        ...emailsRes.buckets.waiting_on_client,
        ...emailsRes.buckets.auto_handled_fyi,
      ];
      // Pick the focus email: the in-place selection, else the
      // highest-priority needs-reply with a draft, else the first
      // need_reply_now card, else nothing.
      const focusId =
        selectedId ??
        pickFocusEmailId(allEmails) ??
        emailsRes.buckets.need_reply_now[0]?.id ??
        null;
      let focusEmail: EmailDetail | null = null;
      if (focusId) {
        try {
          focusEmail = await getEmailDetail(focusId);
        } catch {
          focusEmail = null;
        }
      }
      setState({
        emails: allEmails,
        tasks: tasksRes.tasks,
        focusEmail,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [selectedId]);

  useEffect(() => {
    let cancelled = false;
    void refresh();
    const interval = window.setInterval(() => {
      if (!cancelled) void refresh();
    }, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [refresh]);

  const vitals = useMemo<VitalSigns>(() => {
    return computeVitals(state.emails);
  }, [state.emails]);

  const needsReplyQueue = useMemo(() => {
    return state.emails
      .filter((e) => e.bucket === "need_reply_now" && e.id !== state.focusEmail?.id)
      .slice(0, 3);
  }, [state.emails, state.focusEmail?.id]);

  const handleApprove = useCallback(
    async (body: string) => {
      if (!state.focusEmail) return;
      setSending(true);
      try {
        await postSend(state.focusEmail.id, { body, body_type: "Text" });
        setSelectedId(null);
        await refresh();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setState((prev) => ({ ...prev, error: message }));
      } finally {
        setSending(false);
      }
    },
    [state.focusEmail, refresh],
  );

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await fetch("/auth/logout", { method: "POST", credentials: "include" });
    } catch {
      /* still bounce */
    }
    window.location.href = "/login";
  }, []);

  return (
    <main className="flex min-h-screen bg-op-base text-op-primary">
      <Sidebar
        items={[
          { to: "/", label: "Cockpit", glyph: "◎" },
          { to: "/lanes", label: "Lanes", glyph: "▤" },
          { to: "/tasks", label: "Tasks", glyph: "☑", badge: state.tasks.length },
          { to: "/digest", label: "Digest", glyph: "△", badge: vitals.overdue },
          { to: "/dashboard", label: "Show everything", glyph: "⊞" },
        ]}
        onSignOut={handleSignOut}
        signingOut={signingOut}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <VitalSignsRibbon vitals={vitals} />

        <div className="flex flex-1 overflow-hidden">
          <div
            data-testid="cockpit-center"
            className="flex min-w-0 flex-1 flex-col gap-4 px-4 py-4 md:px-6"
          >
            {state.error ? (
              <div
                role="alert"
                data-testid="cockpit-error"
                className="rounded border border-op-border bg-op-elevated px-3 py-2 text-xs text-op-secondary"
              >
                {state.error}
              </div>
            ) : null}

            {state.loading && !state.focusEmail ? (
              <p
                data-testid="cockpit-loading"
                className="rounded border border-dashed border-op-border px-4 py-10 text-center text-sm text-op-muted"
              >
                Loading cockpit…
              </p>
            ) : state.focusEmail ? (
              <FocusPane
                email={
                  draftOverride && state.focusEmail.draft
                    ? {
                        ...state.focusEmail,
                        draft: { ...state.focusEmail.draft, body: draftOverride },
                      }
                    : state.focusEmail
                }
                onApprove={handleApprove}
                onRefineClick={() => setRefineOpen(true)}
                sending={sending}
              />
            ) : (
              <p
                data-testid="cockpit-empty"
                className="rounded border border-dashed border-op-border px-4 py-10 text-center text-sm text-op-muted"
              >
                Nothing in the queue right now. Showing emails from the last 7 days.
              </p>
            )}

            {needsReplyQueue.length > 0 ? (
              <section
                data-testid="cockpit-queue"
                className="space-y-2"
                aria-label={`${needsReplyQueue.length} more in queue`}
              >
                <h2 className="text-[10px] uppercase tracking-wider text-op-muted">
                  {needsReplyQueue.length} more in queue
                </h2>
                {needsReplyQueue.map((email) => (
                  <CaseCard
                    key={email.id}
                    email={email}
                    onSelect={setSelectedId}
                    variant="compact"
                  />
                ))}
              </section>
            ) : null}

            <BottomTasksStrip tasks={state.tasks} />
          </div>

          <ContextRail email={state.focusEmail} />
        </div>

        <BottomNav
          items={[
            { to: "/", label: "Cockpit", glyph: "◎" },
            { to: "/lanes", label: "Lanes", glyph: "▤" },
            { to: "/tasks", label: "Tasks", glyph: "☑", badge: state.tasks.length },
            { to: "/digest", label: "Esc.", glyph: "△", badge: vitals.overdue },
          ]}
        />
      </div>

      {refineOpen && state.focusEmail && state.focusEmail.draft ? (
        <RefineOverlay
          emailId={state.focusEmail.id}
          initialBody={draftOverride ?? state.focusEmail.draft.body}
          onClose={() => setRefineOpen(false)}
          onApply={(body) => {
            setDraftOverride(body);
            setRefineOpen(false);
          }}
        />
      ) : null}
    </main>
  );
}

function pickFocusEmailId(emails: EmailCardData[]): string | null {
  // Prefer P0 needs-reply, then P1, then anything needs_reply, else
  // the first need_reply_now card.
  const priorityOrder = ["P0", "P1", "P2", "P3"] as const;
  for (const prio of priorityOrder) {
    const match = emails.find(
      (e) => e.bucket === "need_reply_now" && e.priority === prio,
    );
    if (match) return match.id;
  }
  return null;
}

function computeVitals(emails: EmailCardData[]): VitalSigns {
  const now = Date.now();
  let overdue = 0;
  let due_today = 0;
  let queue_depth = 0;
  let escalation_level: VitalSigns["escalation_level"] = "ok";
  for (const e of emails) {
    if (e.bucket === "auto_handled_fyi") continue;
    queue_depth += 1;
    if (e.received_at) {
      const ageH = (now - new Date(e.received_at).getTime()) / 36e5;
      if (e.bucket === "need_reply_now" && ageH > 24) overdue += 1;
      else if (ageH < 12) due_today += 1;
    }
    if (e.triage_state === "ESCALATED") escalation_level = "critical";
    else if (escalation_level === "ok" && e.priority === "P0")
      escalation_level = "active";
  }
  return {
    sla_label: "live",
    queue_depth,
    overdue,
    due_today,
    escalation_level,
  };
}

function BottomTasksStrip({ tasks }: { tasks: CockpitTask[] }) {
  if (tasks.length === 0) {
    return (
      <section
        data-testid="cockpit-tasks-strip"
        className="rounded border border-dashed border-op-border px-3 py-3 text-xs text-op-muted"
      >
        No open tasks. Sonnet extracts them from drafted replies.
      </section>
    );
  }
  return (
    <section
      data-testid="cockpit-tasks-strip"
      className="rounded border border-op-border bg-op-surface px-3 py-3"
      aria-label={`${tasks.length} open tasks`}
    >
      <h2 className="mb-2 text-[10px] uppercase tracking-wider text-op-muted">
        Today's tasks
      </h2>
      <div className="space-y-1.5">
        {tasks.slice(0, 6).map((t) => (
          <TaskRow key={t.id} task={t} />
        ))}
      </div>
    </section>
  );
}
